/**
 * Emma #14 — manual account status tags (AirTable-style), shared UK + AUS.
 *   • useAccountStatuses() — catalogue from Portal Settings (cached per page)
 *   • <StatusTagChips> — read-only chips for list rows / file header
 *   • <StatusTagsButton> — "+ Status" dialog with checkboxes on the customer file
 */
import { useEffect, useState } from "react";
import { Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { getAccountStatuses, setCaseManualStatuses } from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

export const TAG_TONES = {
  rose:    "bg-rose-50 text-rose-800 border-rose-300",
  amber:   "bg-amber-50 text-amber-900 border-amber-300",
  indigo:  "bg-indigo-50 text-indigo-800 border-indigo-300",
  sky:     "bg-sky-50 text-sky-800 border-sky-300",
  slate:   "bg-slate-100 text-slate-700 border-slate-300",
  violet:  "bg-violet-50 text-violet-800 border-violet-300",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-300",
  orange:  "bg-orange-50 text-orange-800 border-orange-300",
  teal:    "bg-teal-50 text-teal-800 border-teal-300",
  fuchsia: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-300",
};

let _cache = null;
export function useAccountStatuses() {
  const [statuses, setStatuses] = useState(_cache || []);
  useEffect(() => {
    let alive = true;
    getAccountStatuses().then(r => {
      _cache = [...(r.statuses || [])].sort((a, b) => (a.label || "").localeCompare(b.label || "", "en", { sensitivity: "base" }));   // alphabetical everywhere (Dean, 8 Oct 2026)
      if (alive) setStatuses(_cache);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return statuses;
}
export const invalidateAccountStatuses = () => { _cache = null; };

export function StatusTagChips({ keys = [], statuses, size = "xs", testid = "status-tag-chip" }) {
  if (!keys?.length) return null;
  const byKey = Object.fromEntries((statuses || []).map(s => [s.key, s]));
  const cls = size === "xs" ? "text-[10px] px-1.5 py-0" : "text-xs px-2 py-0.5";
  return (
    <>
      {keys.map(k => {
        const s = byKey[k];
        return (
          <span key={k} data-testid={testid} title={s?.label || k}
            className={`inline-flex items-center rounded border font-medium whitespace-nowrap ${cls} ${TAG_TONES[s?.color] || TAG_TONES.slate}`}>
            {s?.label || k}
          </span>
        );
      })}
    </>
  );
}

export function StatusTagsButton({ caseRow, onChanged }) {
  const statuses = useAccountStatuses();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(() => new Set(caseRow.manual_statuses || []));
  const current = caseRow.manual_statuses || [];

  const openDialog = () => { setPicked(new Set(current)); setOpen(true); };
  const toggle = (k) => setPicked(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const save = async () => {
    setBusy(true);
    try {
      const r = await setCaseManualStatuses(caseRow.case_id, Array.from(picked));
      toast.success(r.added?.length || r.removed?.length ? "Status tags updated" : "No changes to status tags");
      setOpen(false);
      await onChanged?.();
    } catch (e) {
      toast.error("Status tags failed: " + errorDetailMessage(e, e.message));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={openDialog}
        className={current.length
          ? "h-8 px-3 text-xs rounded-md border border-fuchsia-300 bg-fuchsia-50 text-fuchsia-900 hover:bg-fuchsia-100 inline-flex items-center gap-1"
          : "h-8 px-3 text-xs rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"}
        title="Manual account status tags (BP Filed, Restructured, …)"
        data-testid="customer-file-status-tags-btn">
        <Tag className="w-3 h-3" /> {current.length ? `Status ×${current.length}` : "+ Status"}
      </button>
      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="sm:max-w-md" data-testid="status-tags-dialog">
          <DialogHeader><DialogTitle>Account status tags</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            Tick every status that applies. The list is managed in Portal Settings → Legal Diary → Account status tags.
          </p>
          <div className="grid grid-cols-2 gap-1.5" data-testid="status-tags-options">
            {statuses.map(s => (
              <label key={s.key}
                className={`flex items-center gap-2 text-xs rounded-md border px-2 py-1.5 cursor-pointer ${picked.has(s.key) ? TAG_TONES[s.color] || TAG_TONES.slate : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                <input type="checkbox" checked={picked.has(s.key)} onChange={() => toggle(s.key)} className="h-3.5 w-3.5 accent-fuchsia-600"
                  data-testid={`status-tag-option-${s.key}`} />
                {s.label}
              </label>
            ))}
            {!statuses.length && <div className="text-xs italic text-muted-foreground col-span-2">No statuses configured yet.</div>}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy} data-testid="status-tags-save">{busy ? "Saving…" : "Save tags"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
